// BudgetProgressPanel — web mirror of mobile BudgetProgressPanel.jsx.
// Family spaces only: single collective progress bar against the
// primary's monthly budget. No who-owes-whom math.

import React from "react";
import { Wallet } from "lucide-react";

const INR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

/**
 * @param {object} props
 * @param {number} props.spent          household total for the month (₹)
 * @param {number} props.budget         household budget (₹)
 * @param {string} props.monthLabel     "June 2026"
 * @param {number} props.memberCount    count
 */
export default function BudgetProgressPanel({
  spent = 0,
  budget = 9000,
  monthLabel = "",
  memberCount = 1,
}) {
  const rawPct = budget > 0 ? (spent / budget) * 100 : 0;
  const cappedPct = Math.min(100, Math.max(0, rawPct));
  const overspend = Math.max(0, spent - budget);
  const remaining = Math.max(0, budget - spent);

  // Tier:  ≤60 % calm mint, 60-90 % saffron, ≥90 % coral
  let barColor = "#3EB489";
  let tierLabel = "On track";
  if (rawPct >= 90) {
    barColor = "#FB7185";
    tierLabel = overspend ? "Over budget" : "Critical";
  } else if (rawPct >= 60) {
    barColor = "#FF6B00";
    tierLabel = "Watch the spend";
  }

  return (
    <div className="cartiq-card" data-testid="budget-progress-panel">
      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-[#2D8C6E]" />
        <h3 className="font-display uppercase text-base text-[#0F172A]">
          Household Budget · {monthLabel || "This Month"}
        </h3>
      </div>

      <div className="mt-4 flex items-end justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
            Spent
          </div>
          <div
            className="mt-1 text-4xl font-extrabold font-mono-num text-[#0F172A] leading-none tracking-tight"
            data-testid="budget-progress-spent"
          >
            {INR(spent)}
          </div>
          <div className="mt-2 text-xs text-[#64748B]">
            of <span className="font-bold text-[#334155]">{INR(budget)}</span>{" "}
            budget · {memberCount} member{memberCount === 1 ? "" : "s"}
          </div>
        </div>
        <div
          className="rounded-full px-3 py-2 bg-[#F1F5F9] font-extrabold text-lg font-mono-num"
          style={{ color: barColor }}
          data-testid="budget-progress-pct"
        >
          {Math.round(rawPct)}%
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-2.5 rounded-full bg-[#F1F5F9] overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${cappedPct}%`, backgroundColor: barColor }}
          data-testid="budget-progress-fill"
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: barColor }}
        />
        <span
          className="text-[11px] font-extrabold uppercase tracking-[0.12em]"
          style={{ color: barColor }}
          data-testid="budget-progress-tier"
        >
          {tierLabel}
        </span>
        <span className="flex-1" />
        <span className="text-xs text-[#64748B] font-bold">
          {overspend > 0 ? `${INR(overspend)} over` : `${INR(remaining)} left`}
        </span>
      </div>

      <p className="mt-3 text-[10px] italic text-[#94A3B8] text-center">
        Collective tracking · no per-member splits.
      </p>
    </div>
  );
}
